import { useMemo, useReducer, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import TableWrapper from '@/components/common/table/TableWrapper'
import IndeterminateCheckbox from '@/components/common/IndeterminateCheckbox'
import StatusPill from '@/components/common/StatusPill'
import Panel from '@/components/common/Panel'
import PaymentMethodLogo from '@/components/billing/PaymentMethodLogo'
import {
  deletePaymentHistory,
  deletePaymentHistoryList,
  getPaymentHistory,
  initialPaymentHistory,
  PaymentHistory,
  ExportFilterPaymentHistory,
  prepareForExport,
} from '@/lib/paymenthistory/PaymentHistory'
import Link from 'next/link'
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid'
import { className2 } from '@/components/common/styles'
import { performExport } from '@/components/common/table/performExport'
import type {
  TableWrapperCellContext,
  TableWrapperColumnDef,
  TableWrapperFeatures,
  TableWrapperHeaderContext,
} from '@/lib/table/table'

function SelectHeader({
  table,
}: Readonly<TableWrapperHeaderContext<PaymentHistory>>) {
  return (
    <div className="px-5">
      <IndeterminateCheckbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={
          table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
        }
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    </div>
  )
}

function SelectCell({
  row,
}: Readonly<TableWrapperCellContext<PaymentHistory>>) {
  return (
    <div className="px-1">
      <IndeterminateCheckbox
        checked={row.getIsSelected()}
        indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    </div>
  )
}

// @@TODO: Better name for component (prevent name clash)
const PaymentHistoryPage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['getPaymentHistory'],
    queryFn: () => getPaymentHistory(),
    placeholderData: [initialPaymentHistory],
  })
  console.log('data=', data)
  const [exportData, setExportData] = useState(
    [] as ExportFilterPaymentHistory[],
  )
  const rerender = useReducer(() => ({}), {})[1]

  const columnHelper = createColumnHelper<
    TableWrapperFeatures,
    PaymentHistory
  >()
  const columns = useMemo<Array<TableWrapperColumnDef<PaymentHistory>>>(
    () => [
      columnHelper.display({
        id: 'select',
        enableSorting: false,
        header: SelectHeader,
        cell: SelectCell,
      }),
      {
        accessorKey: 'payment_date',
        id: 'payment_date',
        header: () => <div>Payment date</div>,
        cell: (info) => <div>{info.getValue<string>()}</div>,
      },
      {
        accessorKey: 'payment_id',
        id: 'payment_id',
        header: () => <div>Payment ID</div>,
        cell: (info) => <div>{info.getValue<string>()}</div>,
      },
      {
        accessorKey: 'payment_method',
        id: 'payment_method',
        header: () => <div>Payment method</div>,
        // @@TODO: Icon
        cell: (info) => {
          return (
            <div className="flex">
              <PaymentMethodLogo paymentMethod={info.getValue() as string} />
              <div className="mr-1" />
              {info.getValue<string>()}
            </div>
          )
        },
      },
      {
        accessorKey: 'amount',
        id: 'amount',
        cell: (info) => <div>{info.getValue<number>()}</div>,
      },
      {
        accessorKey: 'status',
        id: 'status',
        cell: (info) => {
          let className = undefined
          let icon = undefined
          const value = info.getValue() as string
          switch (value) {
            case 'success':
              className = 'bg-white dark:bg-black'
              icon = (
                <CheckCircleIcon className="text-green-500 h-4 w-4 mt-0 mx-1" />
              )
              break
            case 'pending':
              className = 'bg-white dark:bg-black'
              icon = <ClockIcon className="text-orange-500 h-4 w-4 mt-0 mx-1" />
              break
            case 'failed':
              className = 'bg-white dark:bg-black'
              icon = (
                <ExclamationCircleIcon className="text-red-500 h-4 w-4 mt-0 mx-1" />
              )
              break
            default:
              className = 'bg-gray-400 dark:bg-gray-600'
              // @@TODO: Log a warning.
              break
          }
          return (
            <div>
              <StatusPill value={value} className={className} icon={icon} />
            </div>
          )
        },
      },
      columnHelper.display({
        id: 'action',
        cell: ({ row }) => (
          <div>
            <button
              type="button"
              className={className2}
              onClick={() => {
                console.log('clicked button for row=', row)
                performExport(prepareForExport([row.original]))
              }}
            >
              Download
            </button>
            <button
              type="button"
              className={className2}
              onClick={() => {
                console.log('row.original.id=', row.original.id)
                const result = deletePaymentHistory(row.original.id)
                console.log('result=', result)
                rerender() // @@TODO: Should not need this if useQuery is used properly
              }}
            >
              Delete
            </button>
          </div>
        ),
      }),
    ],
    [columnHelper, rerender],
  )

  return (
    <div className="p-4">
      <Panel title="Payment history">
        <div>
          <TableWrapper<PaymentHistory>
            data={data as PaymentHistory[]}
            columns={columns}
            exportData={exportData}
            deleteWithList={deletePaymentHistoryList}
            prepareForExport={prepareForExport}
            name="payment history"
          />
          <div className="pb-8">
            <Link href="#!">
              <div className="text-blue-600 dark:text-blue-400 hover:underline decoration-2">
                Read more about our payment methods
              </div>
            </Link>
          </div>
        </div>
      </Panel>
    </div>
  )
}

export default PaymentHistoryPage
